import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import { useTeamContext } from '../../context/TeamContext';
import TeamMemberRoleSelect from './TeamMemberRoleSelect';
import { useOrgDetail } from './useOrgDetail';

export default function TeamSettingsPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { apiClient, user } = useContext(AuthContext);
  const { refresh, setActiveOrgId, activeOrgId } = useTeamContext();
  const { detail, canManageMemberRoles } = useOrgDetail(apiClient, orgId, user);
  const [visibility, setVisibility] = useState('PUBLIC');
  const [displayName, setDisplayName] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [members, setMembers] = useState([]);
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [message, setMessage] = useState('');
  const [roleError, setRoleError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const canDeleteTeam = Boolean(detail?.canDeleteTeam ?? detail?.isOwner);

  const loadMembers = async () => {
    const res = await apiClient.get(`/api/organizations/${orgId}/members`);
    setMembers(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    if (!detail) return;
    setVisibility(detail.leaderboardVisibility || 'PUBLIC');
    setDisplayName(detail.displayName || detail.name || '');
  }, [detail]);

  useEffect(() => {
    loadMembers().catch(() => {});
  }, [apiClient, orgId]);

  const save = async (e) => {
    e.preventDefault();
    await apiClient.patch(`/api/organizations/${orgId}/settings`, {
      displayName,
      leaderboardVisibility: visibility,
    });
    setMessage('Settings saved.');
    await refresh();
  };

  const changeRole = async (memberUserId, newRole) => {
    setRoleError('');
    setRoleSavingId(memberUserId);
    try {
      await apiClient.patch(`/api/organizations/${orgId}/members/${memberUserId}`, { role: newRole });
      await loadMembers();
      await refresh();
      setMessage('Member role updated.');
    } catch (err) {
      setRoleError(err.response?.data?.detail || err.response?.data?.message || 'Could not update role.');
    } finally {
      setRoleSavingId(null);
    }
  };

  const deleteTeam = async () => {
    setDeleteError('');
    const label = displayName.trim();
    if (confirmName.trim() !== label) {
      setDeleteError(`Type "${label}" to confirm deletion.`);
      return;
    }
    if (!window.confirm(`Permanently delete "${label}"? Members, assignments, and data cannot be recovered.`)) {
      return;
    }
    setDeleting(true);
    try {
      await apiClient.delete(`/api/organizations/${orgId}`);
      if (Number(orgId) === activeOrgId) {
        setActiveOrgId(null);
      }
      await refresh();
      navigate('/teams');
    } catch (err) {
      setDeleteError(err.response?.data?.message || err.response?.data?.detail || 'Could not delete team.');
    } finally {
      setDeleting(false);
    }
  };

  const confirmMatches = confirmName.trim() === displayName.trim() && displayName.trim().length > 0;

  return (
    <div className="teams-settings">
      <div className="teams-card" style={{ maxWidth: 480 }}>
        <h2>Organization settings</h2>
        {message && <p className="teams-sub" role="status">{message}</p>}
        <form onSubmit={save}>
          <div className="teams-form-row">
            <label htmlFor="display-name">Display name</label>
            <input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="teams-form-row">
            <label htmlFor="lb-vis">Leaderboard privacy</label>
            <select id="lb-vis" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
              <option value="PUBLIC">Public within organization</option>
              <option value="ANONYMOUS">Anonymous names</option>
              <option value="HIDDEN">Hidden ranks</option>
            </select>
          </div>
          <button type="submit" className="teams-btn">Save</button>
        </form>
      </div>

      <div className="teams-card teams-settings-roles">
        <h2>Member roles</h2>
        {canManageMemberRoles ? (
          <>
            <p className="teams-sub">
              Owners, admins, and managers can change roles here. The workspace owner role is fixed.
            </p>
            {roleError && <p className="teams-error" role="alert">{roleError}</p>}
            <div className="teams-table-wrap">
              <table className="teams-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Email</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="teams-sub">No members loaded yet.</td>
                    </tr>
                  ) : (
                    members.map((m) => (
                      <tr key={m.userId}>
                        <td>{m.name || '—'}</td>
                        <td>{m.email || '—'}</td>
                        <td>
                          <TeamMemberRoleSelect
                            member={m}
                            disabled={roleSavingId === m.userId}
                            onChange={changeRole}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="teams-sub">
            Only the workspace owner, admin, or manager can assign roles. Your role:{' '}
            <strong>{detail?.role || 'member'}</strong>.
            {' '}Open <strong>Teams → Settings</strong> (this page) while signed in with a manager+ account.
          </p>
        )}
      </div>

      {canDeleteTeam && (
        <div className="teams-card teams-danger-zone" style={{ maxWidth: 480 }}>
          <h2>Delete team</h2>
          <p className="teams-sub">
            Permanently remove this workspace, all members, assignments, challenges, and history. Only the owner can do this.
          </p>
          {deleteError && (
            <p className="teams-sub" role="alert" style={{ color: '#b91c1c' }}>{deleteError}</p>
          )}
          <div className="teams-form-row">
            <label htmlFor="confirm-delete-name">
              Type <strong>{displayName || 'team name'}</strong> to confirm
            </label>
            <input
              id="confirm-delete-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              autoComplete="off"
              placeholder={displayName}
            />
          </div>
          <button
            type="button"
            className="teams-btn teams-btn--danger"
            onClick={deleteTeam}
            disabled={deleting || !confirmMatches}
          >
            {deleting ? 'Deleting…' : 'Delete team permanently'}
          </button>
        </div>
      )}
    </div>
  );
}
